// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a cited passage in the Agents prompt box (Cite). The text holds it as Markdown `> ` lines
// and the empty line under it; the editor shows it as one row (CitedQuote) that opens to the whole
// passage. A line break always follows it, so what is typed next starts on the line under it.

import {CitedQuote} from '@app/features/cited_quote/CitedQuote';
import {quoteMarkdown} from '@app/features/cited_quote/QuoteText';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
	$getNodeByKey,
	$isLineBreakNode,
	DecoratorNode,
	type DOMExportOutput,
	type LexicalNode,
	type NodeKey,
	type SerializedLexicalNode,
	type Spread,
} from 'lexical';
import type {JSX} from 'react';

export type SerializedComposerQuoteNode = Spread<{text: string}, SerializedLexicalNode>;

/** Takes a quote out with the line break under it, so no empty line is left behind. */
export function $removeComposerQuote(node: ComposerQuoteNode): void {
	const next = node.getNextSibling();
	if ($isLineBreakNode(next)) next.remove();
	node.remove();
}

function ComposerQuote({text, nodeKey}: {text: string; nodeKey: NodeKey}) {
	const [editor] = useLexicalComposerContext();
	return (
		<CitedQuote
			text={text}
			variant="composer"
			onRemove={() =>
				editor.update(() => {
					const node = $getNodeByKey(nodeKey);
					if ($isComposerQuoteNode(node)) $removeComposerQuote(node);
				})
			}
			flx="lexical.composer.quote"
		/>
	);
}

export class ComposerQuoteNode extends DecoratorNode<JSX.Element> {
	__text: string;

	static override getType(): string {
		return 'composer-quote';
	}

	static override clone(node: ComposerQuoteNode): ComposerQuoteNode {
		return new ComposerQuoteNode(node.__text, node.__key);
	}

	static override importJSON(serializedNode: SerializedComposerQuoteNode): ComposerQuoteNode {
		return $createComposerQuoteNode(serializedNode.text);
	}

	constructor(text: string, key?: NodeKey) {
		super(key);
		this.__text = text;
	}

	override exportJSON(): SerializedComposerQuoteNode {
		return {...super.exportJSON(), text: this.__text};
	}

	override createDOM(): HTMLElement {
		const element = document.createElement('span');
		// A full-width inline block: the line break after it ends its line instead of drawing an empty one.
		element.style.display = 'inline-block';
		element.style.width = '100%';
		element.style.verticalAlign = 'top';
		element.setAttribute('data-lexical-quote', '');
		return element;
	}

	override updateDOM(): boolean {
		return false;
	}

	override exportDOM(): DOMExportOutput {
		const element = document.createElement('span');
		element.textContent = this.getTextContent();
		return {element};
	}

	/** The quote's `> ` lines and the end of its last line; the line break after it makes the empty line. */
	override getTextContent(): string {
		return `${quoteMarkdown(this.getLatest().__text)}\n`;
	}

	getText(): string {
		return this.getLatest().__text;
	}

	override isInline(): true {
		return true;
	}

	override isKeyboardSelectable(): false {
		return false;
	}

	override decorate(): JSX.Element {
		return <ComposerQuote text={this.__text} nodeKey={this.__key} />;
	}
}

export function $createComposerQuoteNode(text: string): ComposerQuoteNode {
	return new ComposerQuoteNode(text);
}

export function $isComposerQuoteNode(node: LexicalNode | null | undefined): node is ComposerQuoteNode {
	return node instanceof ComposerQuoteNode;
}
